import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { isAdminFromToken } from "../../../utils/authToken";

import CreateStoreModal from "../modal/CreateStoreModal";
import CreateProductModal from "../modal/CreateProductModal";
import ReportModal, { EMPTY_REPORT_FILTERS, ReportFilters } from "../modal/ReportModal";
import { uploadFefoFile, uploadShelfControlFile, getRelatorioFefoExcel, getRelatorioShelfExcel } from "../fefoApi";
import { HOMEPAGE } from "../../home/routes";
import { PROCESSOS_ZE_PAGE } from "../../processos-ze/routes";
import "../../dp/pages/DpEco.css";
import "./fefo.css";

const downloadBlob = (data: BlobPart, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const errorMessage = (err: unknown, fallback: string) =>
  err && typeof err === "object" && "message" in err && typeof (err as any).message === "string" && (err as any).message
    ? (err as any).message
    : fallback;

type UploadCardProps = {
  id: string;
  title: string;
  description: string;
  buttonLabel: string;
  upload: (file: File) => Promise<BlobPart>;
  resultName: string;
  successMessage: string;
  errorFallback: string;
};

/** Um cartão por leitura: escolhe (ou solta) a planilha, envia e baixa o resultado. */
const UploadCard: React.FC<UploadCardProps> = ({
  id,
  title,
  description,
  buttonLabel,
  upload,
  resultName,
  successMessage,
  errorFallback,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | undefined | null) => {
    if (!f) return;
    setFile(f);
    setError(null);
  };

  const clear = () => {
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSend = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const result = await upload(file);
      downloadBlob(result, `${Date.now()}_${resultName}.xlsx`);
      toast.success(successMessage);
      clear();
    } catch (err) {
      setError(errorMessage(err, errorFallback));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="ecoCard fefoUpload" aria-labelledby={`${id}-title`}>
      <div className="fefoUpload__head">
        <h3 id={`${id}-title`} className="fefoUpload__title">
          {title}
        </h3>
        <p className="fefoUpload__desc">{description}</p>
      </div>

      <label
        htmlFor={`${id}-file`}
        className={`fefoDrop${dragging ? " is-dragging" : ""}${file ? " has-file" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files?.[0]);
        }}
      >
        <span className="fefoDrop__icon" aria-hidden="true">
          <EcoIcon name={file ? "file" : "download"} size={22} />
        </span>
        {file ? (
          <span className="fefoDrop__text">
            <strong className="fefoDrop__name">{file.name}</strong>
            <span>Clique para trocar o arquivo</span>
          </span>
        ) : (
          <span className="fefoDrop__text">
            <strong>Arraste a planilha aqui</strong>
            <span>ou clique para escolher (.xlsx)</span>
          </span>
        )}
        <input
          ref={inputRef}
          id={`${id}-file`}
          type="file"
          accept=".xlsx"
          className="ecoVisuallyHidden"
          onChange={(e) => pick(e.target.files?.[0])}
        />
      </label>

      {error ? (
        <p className="fefoError" role="alert">
          {error}
        </p>
      ) : null}

      <div className="fefoUpload__actions">
        {file ? (
          <button type="button" className="ecoBtn" onClick={clear} disabled={loading}>
            Remover arquivo
          </button>
        ) : null}
        <button type="button" className="ecoBtn ecoBtn--primary" onClick={handleSend} disabled={!file || loading}>
          {loading ? "Enviando..." : buttonLabel}
        </button>
      </div>
    </section>
  );
};

type ReportCard = { key: "FEFO" | "SHELF"; title: string; description: string; icon: EcoIconName };

const REPORTS: ReportCard[] = [
  {
    key: "FEFO",
    title: "Relatório FEFO",
    description: "Leituras de FEFO por período e loja, em Excel.",
    icon: "thermometer",
  },
  {
    key: "SHELF",
    title: "Relatório Shelf",
    description: "Leituras de prateleira por período e loja, em Excel.",
    icon: "layers",
  },
];

const FefoPage: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  // O hub Processos Zé é só do ADMIN; os demais voltam para a própria home.
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  const goBack = useGoBack(isAdmin ? PROCESSOS_ZE_PAGE() : HOMEPAGE());

  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [openReport, setOpenReport] = useState<"FEFO" | "SHELF" | null>(null);
  const [reportFilters, setReportFilters] = useState<Record<"FEFO" | "SHELF", ReportFilters>>({
    FEFO: EMPTY_REPORT_FILTERS,
    SHELF: EMPTY_REPORT_FILTERS,
  });

  const handleGenerateReport = async (type: "FEFO" | "SHELF", filters: ReportFilters) => {
    setReportFilters((prev) => ({ ...prev, [type]: filters }));
    const params = {
      inicio: filters.inicio || undefined,
      fim: filters.fim || undefined,
      lojas: filters.lojas.length ? filters.lojas : undefined,
    };
    try {
      const data =
        type === "FEFO" ? await getRelatorioFefoExcel(params) : await getRelatorioShelfExcel(params);
      downloadBlob(data, `${Date.now()}_relatorio_${type === "FEFO" ? "fefo" : "shelf"}.xlsx`);
    } catch (err) {
      toast.error(errorMessage(err, `Erro ao gerar o relatório ${type === "FEFO" ? "FEFO" : "Shelf"}.`));
      throw err;
    }
  };

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            ...(isAdmin ? [{ label: "Processos Zé", onClick: () => navigate(PROCESSOS_ZE_PAGE()) }] : []),
            { label: "Controle de bebidas" },
          ]}
          title="Sistema de Controle de Bebidas"
          subtitle="Leitura das planilhas de FEFO e de prateleira (Shelf), e relatórios por período e loja."
          onBack={goBack}
          backLabel="Voltar"
          actions={
            <>
              {isAdmin ? (
                <button type="button" className="ecoBtn" onClick={() => setIsStoreModalOpen(true)}>
                  <EcoIcon name="store" size={18} />
                  Cadastrar loja
                </button>
              ) : null}
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => setIsProductModalOpen(true)}>
                <EcoIcon name="plus" size={18} />
                Cadastrar produto
              </button>
            </>
          }
        />

        <section className="dpeSection" aria-labelledby="fefo-leituras">
          <h2 id="fefo-leituras" className="dpeSection__title">
            Gerar leitura
          </h2>
          <div className="fefoUploads">
            <UploadCard
              id="fefo-leitura"
              title="Leitura FEFO"
              description="Envie a planilha de contagem; o resultado com os indicadores de FEFO é baixado em Excel."
              buttonLabel="Gerar leitura FEFO"
              upload={uploadFefoFile}
              resultName="resultado_fefo"
              successMessage="Leitura FEFO gerada."
              errorFallback="Erro ao processar o arquivo FEFO. Tente novamente."
            />
            <UploadCard
              id="shelf-leitura"
              title="Leitura Shelf"
              description="Envie a planilha de controle de prateleira; o resultado é baixado em Excel."
              buttonLabel="Gerar leitura Shelf"
              upload={uploadShelfControlFile}
              resultName="resultado_shelf"
              successMessage="Leitura Shelf gerada."
              errorFallback="Erro ao processar o arquivo de controle de Shelf. Tente novamente."
            />
          </div>
        </section>

        <section className="dpeSection" aria-labelledby="fefo-relatorios">
          <h2 id="fefo-relatorios" className="dpeSection__title">
            Relatórios por filtro
          </h2>
          <div className="dpeModules">
            {REPORTS.map((r) => (
              <button key={r.key} type="button" className="dpeModule" onClick={() => setOpenReport(r.key)}>
                <span className="dpeModule__icon">
                  <EcoIcon name={r.icon} size={20} strokeWidth={1.8} />
                </span>
                <span className="dpeModule__text">
                  <span className="dpeModule__title">{r.title}</span>
                  <span className="dpeModule__desc">{r.description}</span>
                </span>
                <EcoIcon name="chevronRight" className="dpeModule__chevron" />
              </button>
            ))}
          </div>
        </section>
      </main>

      <CreateStoreModal isOpen={isStoreModalOpen} onClose={() => setIsStoreModalOpen(false)} />
      <CreateProductModal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} />

      <ReportModal
        isOpen={openReport !== null}
        reportType={openReport ?? "FEFO"}
        filters={reportFilters[openReport ?? "FEFO"]}
        onClose={() => setOpenReport(null)}
        onGenerateReport={(filters) => handleGenerateReport(openReport ?? "FEFO", filters)}
      />
    </div>
  );
};

export default FefoPage;
